import React, { useState, useEffect, useCallback } from 'react';
import { Calendar as CalendarIcon, Clock, Building2, User, Phone, Mail, MessageSquare, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ALL_SLOTS = ["11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

const BookingSection = () => {
  const [date, setDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState(ALL_SLOTS);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    message: '',
  });
  const [submitState, setSubmitState] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const fetchSlots = useCallback(async (selectedDate) => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await fetch(`${API_URL}/api/appointments/slots?date=${dateStr}`);
      const data = await res.json();
      setAvailableSlots(data.available_slots || []);
    } catch {
      setAvailableSlots(ALL_SLOTS);
    }
    setLoadingSlots(false);
  }, []);

  useEffect(() => {
    if (date) {
      fetchSlots(date);
      setSelectedTime(null);
    }
  }, [date, fetchSlots]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !selectedTime || !formData.companyName || !formData.contactPerson || !formData.phone) return;

    setSubmitState('loading');
    setErrorMessage('');

    try {
      const res = await fetch(`${API_URL}/api/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: format(date, 'yyyy-MM-dd'),
          time: selectedTime,
          company_name: formData.companyName,
          contact_name: formData.contactPerson,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
        }),
      });

      if (res.ok) {
        setSubmitState('success');
      } else {
        const data = await res.json();
        setErrorMessage(data.detail || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
        setSubmitState('error');
        if (res.status === 409) fetchSlots(date);
      }
    } catch {
      setErrorMessage('Verbindungsfehler. Bitte versuche es erneut.');
      setSubmitState('error');
    }
  };

  const resetForm = () => {
    setSubmitState('idle');
    setFormData({ companyName: '', contactPerson: '', phone: '', email: '', message: '' });
    setDate(null);
    setSelectedTime(null);
    setErrorMessage('');
  };

  const isFormValid = date && selectedTime && formData.companyName && formData.contactPerson && formData.phone;

  if (submitState === 'success') {
    return (
      <section id="booking" data-testid="booking-section" className="py-12 lg:py-20 relative">
        <div className="max-w-3xl mx-auto px-4 lg:px-8">
          <div className="text-center p-10 rounded-3xl bg-white/[0.02] border border-[#78d692]/20">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#78d692]/15 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-[#78d692]" />
            </div>
            <h3 data-testid="booking-success" className="text-xl font-bold mb-3 text-white">Erstgespräch erfolgreich angefragt</h3>
            <p className="text-gray-500 text-sm mb-6">
              Vielen Dank – wir haben deine Terminanfrage erhalten.
              {formData.email && ' Die Bestätigung wurde per E-Mail gesendet.'}
              {' '}Wir melden uns zum vereinbarten Zeitpunkt bei dir.
            </p>
            <button onClick={resetForm} className="text-sm text-[#ff84e4] hover:text-[#ffd1e7] transition-colors">
              Weiteren Termin buchen
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" data-testid="booking-section" className="py-12 lg:py-20 relative">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[300px] h-[300px] bg-[#ff84e4]/3 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8 relative z-10">
        <div className="text-center mb-8 lg:mb-12">
          <span className="inline-block px-4 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-full text-sm text-gray-400 mb-4 lg:mb-5 tracking-wide">
            Erstgespräch
          </span>
          <h2 className="text-2xl lg:text-4xl font-bold mb-3 lg:mb-4 text-white">
            Unverbindliches <span className="text-gradient">Erstgespräch</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm lg:text-base leading-relaxed">
            Wir besprechen deine Produkte, deinen Content-Bedarf und ob sich ein
            Testprojekt oder eine laufende Zusammenarbeit für deine Brand lohnt.
          </p>
        </div>

        {/* Error banner */}
        {submitState === 'error' && (
          <div data-testid="booking-error" className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3 max-w-3xl mx-auto">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-300 text-sm">{errorMessage}</p>
              <button onClick={() => setSubmitState('idle')} className="text-xs text-red-400 hover:text-red-300 mt-1">Schließen</button>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Calendar Side */}
          <div className="p-6 lg:p-8 rounded-3xl bg-white/[0.02] border border-white/[0.06]">
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-3 text-white">
              <CalendarIcon className="w-5 h-5 text-[#ff84e4]" />
              Datum & Uhrzeit wählen
            </h3>

            <div className="mb-6">
              <label className="block text-sm text-gray-500 mb-2">Datum</label>
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    data-testid="booking-date-picker"
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-white/[0.03] border-white/[0.08] hover:bg-white/[0.06] h-12"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#ff84e4]" />
                    {date ? format(date, 'PPP', { locale: de }) : 'Datum auswählen'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#151515] border-white/10" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(newDate) => {
                      setDate(newDate);
                      setIsCalendarOpen(false);
                    }}
                    disabled={(d) => d < new Date() || d.getDay() === 0 || d.getDay() === 6}
                    className="rounded-lg"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="block text-sm text-gray-500 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Verfügbare Uhrzeiten
                {loadingSlots && <span className="text-xs text-gray-600 ml-1">laden...</span>}
              </label>
              {!date ? (
                <p className="text-xs text-gray-600">Bitte zuerst ein Datum wählen</p>
              ) : availableSlots.length === 0 ? (
                <p className="text-xs text-gray-500">Keine freien Termine an diesem Tag. Bitte wähle ein anderes Datum.</p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {ALL_SLOTS.map((time) => {
                    const isAvailable = availableSlots.includes(time);
                    const isSelected = selectedTime === time;
                    return (
                      <button
                        key={time}
                        data-testid={`booking-time-${time}`}
                        onClick={() => isAvailable && setSelectedTime(time)}
                        disabled={!isAvailable}
                        className={`py-2.5 rounded-xl text-xs font-medium transition-colors duration-200 ${
                          isSelected
                            ? 'bg-[#ff84e4] text-black'
                            : isAvailable
                              ? 'bg-white/[0.03] text-gray-400 hover:bg-white/[0.06] border border-white/[0.06]'
                              : 'bg-white/[0.01] text-gray-700 cursor-not-allowed line-through border border-white/[0.03]'
                        }`}
                      >
                        {time}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Form Side */}
          <form onSubmit={handleSubmit} className="p-6 lg:p-8 rounded-3xl bg-white/[0.02] border border-white/[0.06]">
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-3 text-white">
              <User className="w-5 h-5 text-[#d987ff]" />
              Kontaktdaten
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-500 mb-2 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Unternehmen *
                </label>
                <input data-testid="booking-company" type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white placeholder-gray-600 focus:border-[#ff84e4]/40 focus:outline-none focus:ring-1 focus:ring-[#ff84e4]/40 transition-all text-sm" placeholder="Brand / Unternehmen" />
              </div>

              <div>
                <label className="block text-sm text-gray-500 mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Ansprechpartner *
                </label>
                <input data-testid="booking-contact" type="text" name="contactPerson" value={formData.contactPerson} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white placeholder-gray-600 focus:border-[#ff84e4]/40 focus:outline-none focus:ring-1 focus:ring-[#ff84e4]/40 transition-all text-sm" placeholder="Vor- und Nachname" />
              </div>

              <div>
                <label className="block text-sm text-gray-500 mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Telefon *
                </label>
                <input data-testid="booking-phone" type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white placeholder-gray-600 focus:border-[#ff84e4]/40 focus:outline-none focus:ring-1 focus:ring-[#ff84e4]/40 transition-all text-sm" placeholder="+49 ..." />
              </div>

              <div>
                <label className="block text-sm text-gray-500 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  E-Mail (optional)
                </label>
                <input data-testid="booking-email" type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white placeholder-gray-600 focus:border-[#ff84e4]/40 focus:outline-none focus:ring-1 focus:ring-[#ff84e4]/40 transition-all text-sm" placeholder="email@unternehmen.de" />
              </div>

              <div>
                <label className="block text-sm text-gray-500 mb-2 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Kurze Info zum Projekt (optional)
                </label>
                <textarea data-testid="booking-message" name="message" value={formData.message} onChange={handleInputChange} rows={3} className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white placeholder-gray-600 focus:border-[#ff84e4]/40 focus:outline-none focus:ring-1 focus:ring-[#ff84e4]/40 transition-all resize-none text-sm" placeholder="Welche Produkte oder Visuals möchtest du umsetzen?" />
              </div>

              <button
                data-testid="booking-submit"
                type="submit"
                disabled={!isFormValid || submitState === 'loading'}
                className={`w-full py-3.5 rounded-full font-semibold text-sm flex items-center justify-center gap-2 transition-colors duration-200 ${
                  isFormValid && submitState !== 'loading'
                    ? 'bg-[#ff84e4] text-black hover:bg-[#ffd1e7]'
                    : 'bg-white/[0.06] text-gray-600 cursor-not-allowed'
                }`}
              >
                <Send size={16} />
                {submitState === 'loading' ? 'Wird gesendet...' : 'Erstgespräch anfragen'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
